import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Report, ReportDraft } from '../models/report.model';
import { REPORTS_SEED } from '../data/reports.seed';
import { InvitationMockService } from '../../invitations/services/invitation-mock.service';
import { OrgMockService } from '../../../service/org-mock.service';
import { composePool, PoolRow } from './report-pool';

// Bumped v1 → v2 when the seed grew from 3 to 14 rows: without a new key, a browser holding the
// old 3-row cache would never see the expanded set and pagination would still never trigger.
const STORAGE_KEY = 'fs_mock_reports_v3';
const COUNTER_KEY = 'fs_mock_reports_counter_v3';

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v));

@Injectable({ providedIn: 'root' })
export class ReportMockService {
  private subject = new BehaviorSubject<Report[]>([]);
  private idCounter = 1;

  /**
   * `invitations` is injected because a report's participants live in the invitations store —
   * Reports owns definitions, not people. `orgs` resolves the owner's display name at create.
   */
  constructor(private invitations: InvitationMockService, private orgs: OrgMockService) {
    const saved = localStorage.getItem(STORAGE_KEY);
    const list: Report[] = saved ? JSON.parse(saved) : clone(REPORTS_SEED);
    this.subject.next(list);
    const mark = +(localStorage.getItem(COUNTER_KEY) ?? '1');
    // Anything but a positive integer falls back to the list-derived counter. The guard is
    // isInteger, not isFinite: Math.max propagates NaN (poisoning idCounter forever, so every
    // create() mints the same literal 'rep-NaN'), and a fractional mark would mint 'rep-4.7'.
    // Only this service writes the key, so a bad value means corruption — recover, don't trust it.
    const usable = Number.isInteger(mark) && mark > 0;
    this.idCounter = usable ? Math.max(this.nextCounter(list), mark) : this.nextCounter(list);
  }

  /** Restore the seed (demos / tests). */
  reset(): void {
    const seed = clone(REPORTS_SEED);
    this.commit(seed);
    this.idCounter = this.nextCounter(seed);
    localStorage.removeItem(COUNTER_KEY);
  }

  list(): Observable<Report[]> { return of(clone(this.subject.value)); }

  getById(id: string): Observable<Report | undefined> {
    return of(clone(this.subject.value.find(r => r.id === id)));
  }

  create(draft: ReportDraft): Observable<Report> {
    const now = this.stamp();
    const report: Report = {
      id: this.newId(),
      title: draft.title.trim(),
      ownerOrgId: draft.ownerOrgId,
      ownerOrgName: this.orgs.getById(draft.ownerOrgId)?.name ?? '',
      type: draft.type,
      sourceOrgIds: [...draft.sourceOrgIds],
      participantIds: [...draft.participantIds],
      createdBy: this.invitations.currentUser().name,
      createdAt: now, updatedAt: now,
    };
    this.commit([...this.subject.value, report]);
    return of(clone(report));
  }

  update(id: string, patch: Partial<ReportDraft>): Observable<Report> {
    return this.mutate(id, r => {
      // Object.assign would alias patch.sourceOrgIds / patch.participantIds by reference,
      // so a caller mutating its own array afterwards would silently corrupt the stored report.
      Object.assign(r, patch);
      if (patch.sourceOrgIds) { r.sourceOrgIds = [...patch.sourceOrgIds]; }
      if (patch.participantIds) { r.participantIds = [...patch.participantIds]; }
      if (patch.ownerOrgId) { r.ownerOrgName = this.orgs.getById(patch.ownerOrgId)?.name ?? ''; }
      r.updatedAt = this.stamp();
    });
  }

  /**
   * Clone a definition under a new id. Arrays are copied, not aliased: two reports sharing one
   * participantIds array would edit each other.
   */
  duplicate(id: string): Observable<Report> {
    const src = this.subject.value.find(r => r.id === id);
    if (!src) { throw new Error(`No report ${id}`); }
    const now = this.stamp();
    const copy: Report = {
      ...clone(src),
      id: this.newId(),
      title: `${src.title} copy`,
      createdBy: this.invitations.currentUser().name,
      createdAt: now, updatedAt: now,
    };
    this.commit([...this.subject.value, copy]);
    return of(clone(copy));
  }

  remove(id: string): Observable<void> {
    this.commit(this.subject.value.filter(r => r.id !== id));
    return of(void 0);
  }

  currentUser(): { name: string; email: string } { return this.invitations.currentUser(); }

  /**
   * Every eligible participant across the given organizations.
   *
   * Archived invitations are excluded: they are out of active use, and their people should not
   * feed new reports. Drafts have no participants, so they contribute nothing either way.
   *
   * The eligibility rule is HANDED IN from InvitationMockService rather than restated — there is
   * one definition of who may appear in a report.
   */
  pool(orgIds: string[]): Observable<PoolRow[]> {
    return this.invitations.list({ archived: false }).pipe(
      map(res => composePool(res.rows, orgIds, inv => this.invitations.eligibleParticipants(inv))),
    );
  }

  // --- internals ---

  private mutate(id: string, fn: (r: Report) => void): Observable<Report> {
    const list = clone(this.subject.value);
    const target = list.find(r => r.id === id);
    if (!target) { throw new Error(`No report ${id}`); }
    fn(target);
    this.commit(list);
    return of(clone(target));
  }

  private commit(list: Report[]): void {
    this.subject.next(list);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  /**
   * Ids count UP from the highest ever seen — never `length + 1`, which re-issues the id of a
   * deleted report and collides with anything still holding it. The list alone can't guarantee
   * that: deleting the highest-numbered report removes the only evidence it existed, so the
   * high-water mark is persisted separately under COUNTER_KEY and never lowered.
   */
  private newId(): string {
    const id = `rep-${this.idCounter++}`;
    localStorage.setItem(COUNTER_KEY, String(this.idCounter));
    return id;
  }

  private nextCounter(list: Report[]): number {
    return list.reduce((max, r) => {
      const m = /^rep-(\d+)$/.exec(r.id);
      return m ? Math.max(max, +m[1] + 1) : max;
    }, 1);
  }

  /** Wall-clock is fine for mutations (unlike seeds, which must stay deterministic). */
  private stamp(): string { return new Date().toISOString(); }
}
